import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { apiFetch } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { useConfirm } from '../components/ui/ConfirmDialog'
import { Modal } from '../components/ui/Modal'
import type { Organization, OrgMember } from '../types'

interface MemberRow extends OrgMember {
  email?: string
  must_change_password?: boolean
}

interface UnassignedUser {
  id: string
  email: string | null
  created_at: string | null
}

interface OrgAssignment {
  org_id: string
  role: 'org_admin' | 'export' | 'viewer'
}

export function AdminPage() {
  const { toast } = useToast()
  const { confirm, dialog: confirmDialog } = useConfirm()
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [members, setMembers] = useState<MemberRow[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)

  // Unassigned users
  const [unassigned, setUnassigned] = useState<UnassignedUser[]>([])
  const [unassignedOpen, setUnassignedOpen] = useState(true)
  const [assignModal, setAssignModal] = useState<UnassignedUser | null>(null)
  const [assignments, setAssignments] = useState<OrgAssignment[]>([])
  const [assigning, setAssigning] = useState(false)

  // Create org state
  const [newOrgName, setNewOrgName] = useState('')
  const [creating, setCreating] = useState(false)

  // Invite state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'org_admin' | 'export' | 'viewer'>('org_admin')
  const [inviting, setInviting] = useState(false)

  // Temp password modal (result display)
  const [tempPasswordModal, setTempPasswordModal] = useState<{ email: string; password: string } | null>(null)

  // Password management modal
  const [pwModal, setPwModal] = useState<MemberRow | null>(null)
  const [pwMode, setPwMode] = useState<'auto' | 'custom'>('auto')
  const [pwCustom, setPwCustom] = useState('')
  const [pwMustChange, setPwMustChange] = useState(true)
  const [pwSaving, setPwSaving] = useState(false)

  const [error, setError] = useState<string | null>(null)

  const loadUnassigned = useCallback(async () => {
    try {
      const data = await apiFetch<UnassignedUser[]>('/api/admin/members?unassigned=1')
      setUnassigned(data ?? [])
    } catch (err) {
      // Don't clear on transient errors
      console.error('loadUnassigned failed:', err)
    }
  }, [])

  const loadOrgs = useCallback(async () => {
    const { data } = await supabase
      .from('organizations')
      .select('*')
      .order('name')
    setOrgs(data ?? [])
    setLoading(false)
  }, [])

  const loadMembers = useCallback(async (orgId: string) => {
    setMembersLoading(true)
    try {
      const data = await apiFetch<MemberRow[]>(
        `/api/admin/members?org_id=${orgId}`
      )
      setMembers(data ?? [])
    } catch (err) {
      // Don't clear members on transient errors (e.g. token refresh race)
      console.error('loadMembers failed:', err)
    } finally {
      setMembersLoading(false)
    }
  }, [])

  useEffect(() => {
    loadOrgs()
    loadUnassigned()
  }, [loadOrgs, loadUnassigned])

  useEffect(() => {
    if (selectedOrg) {
      setMembers([]) // clear stale data from previous org
      loadMembers(selectedOrg.id)
    } else {
      setMembers([])
    }
  }, [selectedOrg, loadMembers])

  async function handleCreateOrg(e: React.FormEvent) {
    e.preventDefault()
    if (!newOrgName.trim()) return
    setCreating(true)
    setError(null)

    const { error: err } = await supabase
      .from('organizations')
      .insert({ name: newOrgName.trim() })

    if (err) {
      setError(err.message)
    } else {
      setNewOrgName('')
      await loadOrgs()
    }
    setCreating(false)
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedOrg || !inviteEmail.trim()) return
    setInviting(true)
    setError(null)

    try {
      const result = await apiFetch<{
        status: string
        email: string
        temp_password?: string
      }>('/api/admin/members', {
        method: 'POST',
        body: JSON.stringify({
          org_id: selectedOrg.id,
          email: inviteEmail.trim(),
          role: inviteRole,
        }),
      })
      setInviteEmail('')
      await loadMembers(selectedOrg.id)

      if (result.status === 'created' && result.temp_password) {
        setTempPasswordModal({ email: result.email, password: result.temp_password })
      } else {
        toast(`${result.email} added to organization`, 'success')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add user')
    } finally {
      setInviting(false)
    }
  }

  function openPasswordModal(member: MemberRow) {
    setPwModal(member)
    setPwMode('auto')
    setPwCustom('')
    setPwMustChange(true)
  }

  async function handleSetPassword() {
    if (!selectedOrg || !pwModal) return
    setPwSaving(true)

    const payload: Record<string, unknown> = {
      user_id: pwModal.user_id,
      org_id: selectedOrg.id,
      must_change_password: pwMustChange,
    }
    if (pwMode === 'custom') {
      if (pwCustom.length < 8) {
        toast('Password must be at least 8 characters', 'error')
        setPwSaving(false)
        return
      }
      payload.password = pwCustom
    }

    try {
      const result = await apiFetch<{ temp_password: string }>('/api/admin/members', {
        method: 'PUT',
        body: JSON.stringify(payload),
      })
      setPwModal(null)
      setTempPasswordModal({
        email: pwModal.email ?? pwModal.user_id,
        password: result.temp_password,
      })
      await loadMembers(selectedOrg.id)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to set password', 'error')
    } finally {
      setPwSaving(false)
    }
  }

  async function handleRoleChange(member: MemberRow, newRole: string) {
    if (!selectedOrg || newRole === member.role) return
    try {
      await apiFetch('/api/admin/members', {
        method: 'PUT',
        body: JSON.stringify({ user_id: member.user_id, org_id: selectedOrg.id, role: newRole }),
      })
      toast(`Role updated to ${newRole.replace('_', ' ')}`, 'success')
      await loadMembers(selectedOrg.id)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update role', 'error')
    }
  }

  async function handleRemoveMember(member: MemberRow) {
    if (!selectedOrg) return
    const ok = await confirm({
      title: 'Remove Member',
      message: `Are you sure you want to remove ${member.email ?? member.user_id} from ${selectedOrg.name}?`,
      confirmLabel: 'Remove',
    })
    if (!ok) return

    try {
      await apiFetch('/api/admin/members', {
        method: 'DELETE',
        body: JSON.stringify({
          member_id: member.id,
          org_id: selectedOrg.id,
        }),
      })
      toast('Member removed', 'success')
      await loadMembers(selectedOrg.id)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove member', 'error')
    }
  }

  async function handleDeleteOrg(orgId: string) {
    const org = orgs.find(o => o.id === orgId)
    const ok = await confirm({
      title: 'Delete Organization',
      message: `Are you sure you want to delete "${org?.name ?? 'this organization'}" and all its data? This cannot be undone.`,
      confirmLabel: 'Delete Organization',
    })
    if (!ok) return

    const { error: err } = await supabase
      .from('organizations')
      .delete()
      .eq('id', orgId)

    if (err) {
      setError(err.message)
    } else {
      if (selectedOrg?.id === orgId) setSelectedOrg(null)
      await loadOrgs()
    }
  }

  function openAssignModal(user: UnassignedUser) {
    setAssignModal(user)
    setAssignments([{ org_id: orgs[0]?.id ?? '', role: 'viewer' }])
  }

  async function handleAssign() {
    if (!assignModal || assignments.length === 0) return
    const valid = assignments.filter(a => a.org_id)
    if (valid.length === 0) return

    setAssigning(true)
    try {
      await apiFetch('/api/admin/members', {
        method: 'PATCH',
        body: JSON.stringify({ user_id: assignModal.id, assignments: valid }),
      })
      toast(`User assigned to ${valid.length} org${valid.length > 1 ? 's' : ''}`, 'success')
      setAssignModal(null)
      await loadUnassigned()
      if (selectedOrg) await loadMembers(selectedOrg.id)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to assign user', 'error')
    } finally {
      setAssigning(false)
    }
  }

  async function handleDeleteUser(user: UnassignedUser) {
    const ok = await confirm({
      title: 'Delete User',
      message: `Permanently delete ${user.email ?? user.id}? This cannot be undone.`,
      confirmLabel: 'Delete',
    })
    if (!ok) return

    try {
      await apiFetch('/api/admin/members', {
        method: 'DELETE',
        body: JSON.stringify({ delete_user_id: user.id }),
      })
      toast('User deleted', 'success')
      await loadUnassigned()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete user', 'error')
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text)
    toast('Copied to clipboard', 'info')
  }

  const inputCls = 'flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const selectCls = 'bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const btnPrimary = 'bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50'
  const btnSmall = 'text-xs text-gray-500 hover:text-gray-900 px-2 py-1 transition-colors'

  if (loading) {
    return <div className="text-brand">Loading organizations...</div>
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <p className="text-gray-500 text-sm mt-1">Manage organizations and users</p>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-lg px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Unassigned users — collapsible */}
      {unassigned.length > 0 && (
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setUnassignedOpen(!unassignedOpen)}
            className="flex items-center gap-2 text-xs uppercase tracking-wide text-warning font-semibold mb-3 hover:text-warning/80 transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${unassignedOpen ? 'rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            Unassigned Users ({unassigned.length})
          </button>
          {unassignedOpen && (
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-3">
                These users have signed in but haven't been assigned to an organization yet.
              </p>
              <div className="space-y-2">
                {unassigned.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{u.email ?? u.id}</p>
                      {u.created_at && (
                        <p className="text-xs text-gray-500">
                          Signed up {new Date(u.created_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openAssignModal(u)}
                        className={btnPrimary}
                      >
                        Assign
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u)}
                        className="text-xs text-gray-400 hover:text-error px-2 py-2 transition-colors"
                        title="Delete user permanently"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Org list */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-wide text-brand-dark font-semibold">
              Organizations ({orgs.length})
            </h2>
          </div>

          <form onSubmit={handleCreateOrg} className="flex gap-2 mb-4">
            <input
              type="text"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="New organization name"
              className={inputCls}
            />
            <button type="submit" disabled={creating} className={btnPrimary}>
              {creating ? '...' : 'Create'}
            </button>
          </form>

          <div className="space-y-2">
            {orgs.map((org) => (
              <div
                key={org.id}
                onClick={() => setSelectedOrg(org)}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedOrg?.id === org.id
                    ? 'bg-brand/10 border-brand/30'
                    : 'bg-card border-card-border hover:border-gray-300'
                }`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{org.name}</p>
                    {org.mi_enabled && (
                      <span className="text-[9px] uppercase tracking-wide bg-brand/10 text-brand px-1.5 py-0.5 rounded font-semibold">MI</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(org.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteOrg(org.id) }}
                  className="text-gray-400 hover:text-error text-sm transition-colors"
                >
                  Delete
                </button>
              </div>
            ))}
            {orgs.length === 0 && (
              <p className="text-gray-500 text-sm">No organizations yet.</p>
            )}
          </div>
        </div>

        {/* Org detail */}
        <div>
          {selectedOrg ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs uppercase tracking-wide text-brand-dark font-semibold">
                  {selectedOrg.name}
                </h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-gray-500">Analytics (MI)</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={selectedOrg.mi_enabled}
                    onClick={async () => {
                      const next = !selectedOrg.mi_enabled
                      const { error } = await supabase
                        .from('organizations')
                        .update({ mi_enabled: next })
                        .eq('id', selectedOrg.id)
                      if (error) { toast(error.message, 'error'); return }
                      setSelectedOrg({ ...selectedOrg, mi_enabled: next })
                      setOrgs(prev => prev.map(o => o.id === selectedOrg.id ? { ...o, mi_enabled: next } : o))
                      toast(`Analytics ${next ? 'enabled' : 'disabled'} for ${selectedOrg.name}`, 'success')
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      selectedOrg.mi_enabled ? 'bg-brand' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      selectedOrg.mi_enabled ? 'translate-x-4.5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </label>
              </div>

              <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">Members</h3>

              <form onSubmit={handleInvite} className="flex gap-2 mb-4">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@email.com"
                  className={inputCls}
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'org_admin' | 'export' | 'viewer')}
                  className={selectCls}
                >
                  <option value="org_admin">Org Admin</option>
                  <option value="export">Export</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button type="submit" disabled={inviting} className={btnPrimary}>
                  {inviting ? '...' : 'Add'}
                </button>
              </form>

              <div className="space-y-2">
                {members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between p-3 bg-card border border-card-border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-medium">{m.email ?? m.user_id}</p>
                        {m.must_change_password && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning font-medium">
                            Temp password
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={m.role}
                        onChange={(e) => handleRoleChange(m, e.target.value)}
                        className="text-xs bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-gray-700 focus:outline-none focus:border-brand"
                      >
                        <option value="rv_admin">RV Admin</option>
                        <option value="org_admin">Org Admin</option>
                        <option value="export">Export</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        onClick={() => openPasswordModal(m)}
                        className={btnSmall}
                        title="Manage password"
                      >
                        Password
                      </button>
                      <button
                        onClick={() => handleRemoveMember(m)}
                        className="text-xs text-gray-400 hover:text-error px-2 py-1 transition-colors"
                        title="Remove from organization"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {membersLoading && (
                  <p className="text-gray-500 text-sm">Loading members...</p>
                )}
                {!membersLoading && members.length === 0 && (
                  <p className="text-gray-500 text-sm">No members yet.</p>
                )}
              </div>
            </>
          ) : (
            <div className="bg-card border border-card-border rounded-lg p-8 text-center text-gray-500">
              <p>Select an organization to manage members</p>
            </div>
          )}
        </div>
      </div>

      {/* Password management modal */}
      <Modal
        open={!!pwModal}
        onClose={() => setPwModal(null)}
        title="Set Password"
      >
        {pwModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Set password for <span className="font-medium text-gray-900">{pwModal.email ?? pwModal.user_id}</span>
            </p>

            {/* Mode toggle */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPwMode('auto')}
                className={`flex-1 text-sm py-2 rounded-md border transition-colors ${
                  pwMode === 'auto'
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
              >
                Auto Generate
              </button>
              <button
                type="button"
                onClick={() => setPwMode('custom')}
                className={`flex-1 text-sm py-2 rounded-md border transition-colors ${
                  pwMode === 'custom'
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
              >
                Custom Password
              </button>
            </div>

            {/* Custom password input */}
            {pwMode === 'custom' && (
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
                  Password
                </label>
                <input
                  type="text"
                  value={pwCustom}
                  onChange={(e) => setPwCustom(e.target.value)}
                  placeholder="Min 8 characters"
                  className={inputCls}
                  autoFocus
                />
              </div>
            )}

            {/* Must change toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => setPwMustChange(!pwMustChange)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  pwMustChange ? 'bg-brand' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  pwMustChange ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </div>
              <span className="text-sm text-gray-700">Require password change on login</span>
            </label>

            <button
              onClick={handleSetPassword}
              disabled={pwSaving || (pwMode === 'custom' && pwCustom.length < 8)}
              className="w-full bg-brand hover:bg-brand-dark text-white text-sm font-medium py-2 rounded-md transition-colors disabled:opacity-50"
            >
              {pwSaving ? 'Saving...' : pwMode === 'auto' ? 'Generate & Set Password' : 'Set Password'}
            </button>
          </div>
        )}
      </Modal>

      {/* Temp password modal */}
      <Modal
        open={!!tempPasswordModal}
        onClose={() => setTempPasswordModal(null)}
        title="Password Set"
      >
        {tempPasswordModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Share these credentials with the user.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Email</label>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gray-900 flex-1">{tempPasswordModal.email}</code>
                  <button
                    onClick={() => copyToClipboard(tempPasswordModal.email)}
                    className="text-xs text-brand hover:text-brand-dark transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Password</label>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gray-900 font-bold flex-1">{tempPasswordModal.password}</code>
                  <button
                    onClick={() => copyToClipboard(tempPasswordModal.password)}
                    className="text-xs text-brand hover:text-brand-dark transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                copyToClipboard(`Email: ${tempPasswordModal.email}\nPassword: ${tempPasswordModal.password}`)
                setTempPasswordModal(null)
              }}
              className="w-full bg-brand hover:bg-brand-dark text-white text-sm font-medium py-2 rounded-md transition-colors"
            >
              Copy All & Close
            </button>
          </div>
        )}
      </Modal>

      {/* Assign user modal */}
      <Modal
        open={!!assignModal}
        onClose={() => setAssignModal(null)}
        title="Assign User to Organization"
      >
        {assignModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Assign <span className="font-medium text-gray-900">{assignModal.email ?? assignModal.id}</span> to one or more organizations.
            </p>

            <div className="space-y-2">
              {assignments.map((a, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select
                    value={a.org_id}
                    onChange={(e) => {
                      const next = [...assignments]
                      next[i] = { ...next[i], org_id: e.target.value }
                      setAssignments(next)
                    }}
                    className={`flex-1 ${selectCls}`}
                  >
                    <option value="">Select org...</option>
                    {orgs.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  <select
                    value={a.role}
                    onChange={(e) => {
                      const next = [...assignments]
                      next[i] = { ...next[i], role: e.target.value as 'org_admin' | 'export' | 'viewer' }
                      setAssignments(next)
                    }}
                    className={selectCls}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="export">Export</option>
                    <option value="org_admin">Org Admin</option>
                  </select>
                  {assignments.length > 1 && (
                    <button
                      onClick={() => setAssignments(assignments.filter((_, j) => j !== i))}
                      className="text-gray-400 hover:text-error text-sm transition-colors px-1"
                      title="Remove"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setAssignments([...assignments, { org_id: '', role: 'viewer' }])}
              className="text-sm text-brand hover:text-brand-dark transition-colors"
            >
              + Add another org
            </button>

            <button
              onClick={handleAssign}
              disabled={assigning || assignments.every(a => !a.org_id)}
              className="w-full bg-brand hover:bg-brand-dark text-white text-sm font-medium py-2 rounded-md transition-colors disabled:opacity-50"
            >
              {assigning ? 'Assigning...' : 'Assign User'}
            </button>
          </div>
        )}
      </Modal>

      {confirmDialog}
    </div>
  )
}
